import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import { Modal } from '../components/Modal.jsx';

const TYPES = ['INVOICE', 'QUOTE', 'PROFORMA', 'CREDIT_NOTE', 'RECURRING_TEMPLATE'];

export default function Series() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ['series'], queryFn: () => api('/api/series') });
  const [editing, setEditing] = useState(null);

  const save = useMutation({
    mutationFn: p => p.id
      ? api(`/api/series/${p.id}`, { method: 'PATCH', body: p.data })
      : api('/api/series', { method: 'POST', body: p.data }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['series'] }); setEditing(null); },
  });

  return (
    <>
      <div className="page-header">
        <h1>{t('series.title')}</h1>
        <button className="btn btn-primary" onClick={() => setEditing({})}>{t('series.add')}</button>
      </div>
      <table className="table">
        <thead>
          <tr>
            <th>{t('series.docType')}</th><th>{t('series.code')}</th><th>{t('series.prefix')}</th>
            <th>{t('series.padding')}</th><th>{t('series.nextNumber')}</th><th>{t('series.default')}</th>
            <th>{t('common.active')}</th><th>{t('common.actions')}</th>
          </tr>
        </thead>
        <tbody>
          {data?.series?.map(s => (
            <tr key={s.id}>
              <td>{s.docType}</td>
              <td><strong>{s.code}</strong></td>
              <td>{s.prefix}{'{n}'}{s.suffix}</td>
              <td>{s.padding}</td>
              <td>{s.nextNumber}</td>
              <td>{s.isDefault ? '✓' : ''}</td>
              <td>{s.active ? t('common.yes') : t('common.no')}</td>
              <td><button className="btn btn-secondary btn-sm" onClick={() => setEditing(s)}>{t('common.edit')}</button></td>
            </tr>
          ))}
        </tbody>
      </table>
      {editing && (
        <SeriesModal series={editing} onClose={() => setEditing(null)} onSave={d => save.mutate({ id: editing.id, data: d })} saving={save.isPending} error={save.error?.data} />
      )}
    </>
  );
}

function SeriesModal({ series, onClose, onSave, saving, error }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    docType: series.docType || 'INVOICE',
    code: series.code || '',
    prefix: series.prefix || '',
    suffix: series.suffix || '',
    padding: series.padding ?? 4,
    nextNumber: series.nextNumber ?? 1,
    isDefault: series.isDefault ?? false,
    active: series.active ?? true,
  });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  return (
    <Modal title={series.id ? t('common.edit') : t('series.add')} onClose={onClose}>
      <form onSubmit={e => { e.preventDefault(); onSave(form); }}>
        {error && <div className="error-banner">{error.error}</div>}
        <div className="row">
          <div className="field"><label>{t('series.docType')}</label>
            <select value={form.docType} onChange={e => set('docType', e.target.value)}>
              {TYPES.map(t => <option key={t}>{t}</option>)}
            </select>
          </div>
          <div className="field"><label>{t('series.code')}</label><input required value={form.code} onChange={e => set('code', e.target.value)} /></div>
        </div>
        <div className="row">
          <div className="field"><label>{t('series.prefix')}</label><input value={form.prefix} onChange={e => set('prefix', e.target.value)} /></div>
          <div className="field"><label>{t('series.suffix')}</label><input value={form.suffix} onChange={e => set('suffix', e.target.value)} /></div>
        </div>
        <div className="row">
          <div className="field"><label>{t('series.padding')}</label><input type="number" min={0} max={10} value={form.padding} onChange={e => set('padding', Number(e.target.value))} /></div>
          <div className="field"><label>{t('series.nextNumber')}</label><input type="number" min={1} value={form.nextNumber} onChange={e => set('nextNumber', Number(e.target.value))} /></div>
        </div>
        <div className="row">
          <div className="field"><label><input type="checkbox" checked={form.isDefault} onChange={e => set('isDefault', e.target.checked)} /> {t('series.default')}</label></div>
          <div className="field"><label><input type="checkbox" checked={form.active} onChange={e => set('active', e.target.checked)} /> {t('common.active')}</label></div>
        </div>
        <div className="modal-actions">
          <button type="button" className="btn btn-secondary" onClick={onClose}>{t('common.cancel')}</button>
          <button type="submit" className="btn btn-primary" disabled={saving}>{t('common.save')}</button>
        </div>
      </form>
    </Modal>
  );
}
